import { ChartDataError, ChartDataErrorReason, MuzeChartRenderer } from '@mode/charts/util-muze';
import { isEmpty } from 'lodash';

export interface DataLimitsResults {
  stats: MuzeChartRenderer.VisualizationDataStats;
  reason: ChartDataErrorReason;
  limit: number;
  actual: number;
  channel?: string;
  fieldId?: string;
  rowPagingEnabled?: boolean;
  rowPageSize?: number;
}

export function toChartDataError(results: DataLimitsResults) {
  return new ChartDataError(results.reason, results.actual, results.limit, results.stats, results.channel);
}

export function checkDataLimits(
  dataStats: MuzeChartRenderer.VisualizationDataStats,
  limits: { facets: number; points: number; series: number; elements: number }
): DataLimitsResults | undefined {
  if (isEmpty(dataStats)) {
    return;
  }

  const { facets: maxFacets, points: maxPoints, series: maxSeries, elements: maxElements } = limits;
  // If row paging is enabled, recalculate the total facet count based on the page size (ie facets per page).
  // We could still exceed the facet limit with paging enabled if the column facets count alone exceeds
  // the facet count limit. (ie colFacets > maxFacets)
  const rowFacets = dataStats.rowFacets ?? 1;
  const rowPagingEnabled = dataStats.requiresRowPaging ?? false;
  const rowFacetsPerPage = rowPagingEnabled ? (dataStats.rowPageSize ?? rowFacets) : rowFacets;
  const facetCount = rowPagingEnabled
    ? rowFacetsPerPage * (dataStats.colFacets ?? 1) * (dataStats.measureCount ?? 1)
    : (dataStats.facetCount ?? 1);

  // Facet count check
  if (facetCount > maxFacets) {
    return {
      stats: dataStats,
      reason: ChartDataErrorReason.TooManyFacets,
      actual: facetCount,
      limit: maxFacets,
      rowPagingEnabled,
      rowPageSize: rowPagingEnabled ? rowFacetsPerPage : undefined,
    };
  }

  // Skip the max point check and max element check when paging since all
  // points/elements may not be on a single page. We will check the element
  // count for each page when we get the paging metadata which has the row counts.
  if (!rowPagingEnabled) {
    // Maximum point check
    if ((dataStats.dataCount ?? 0) > maxPoints) {
      return {
        stats: dataStats,
        reason: ChartDataErrorReason.TooManyPoints,
        actual: dataStats.dataCount ?? 0,
        limit: maxPoints,
      };
    }
    // Maximum elements check
    if ((dataStats.elementCount ?? 0) > maxElements) {
      return {
        stats: dataStats,
        reason: ChartDataErrorReason.TooManyElements,
        actual: dataStats.elementCount ?? 0,
        limit: maxElements,
      };
    }
  }

  // Check for any series that exceeds the limit
  let exceededSeries;
  (dataStats.layers ?? []).some((layer) => {
    const maxChannel = Object.entries(layer).reduce(
      (maxSeries, [key, value]) => {
        if (value != null && (value.value ?? 0) > (maxSeries.count ?? 0)) {
          return { channel: key, id: value.id, count: value.value ?? 0 };
        }
        return maxSeries;
      },
      {} as { channel: string | undefined; id: string | undefined; count: number | undefined }
    );

    if ((maxChannel.count ?? 0) > maxSeries) {
      exceededSeries = {
        stats: dataStats,
        reason: ChartDataErrorReason.TooManySeries,
        actual: maxChannel.count ?? 0,
        limit: maxSeries,
        channel: maxChannel.channel,
        fieldId: maxChannel.id,
      };
      return true;
    }
  });
  return exceededSeries;
}
